import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Text, useTheme } from '@Sage/hoi-poi-ui';
import { FiltersProvider } from '@Sage/ssm-web5';
import { Route, Routes, Navigate } from 'react-router-dom';

import { OrdersActions, EntityActions, EntityDetailActions, EntityCrudActions } from 'actions';
import { SALESORDERS } from 'constants/Entities';
import { ensureRoute, RouteOnMount } from 'utils/routes';
import { getLiteral } from 'utils/getLiteral';
import { formatCompactDecimalByConfig, DECIMAL_TYPES } from 'utils/decimal';

import useEntityDetail from 'containers/components/EntityDetail/hooks/useEntityDetail';
import ContentLayout from 'containers/components/layouts/ContentLayout';
import DetailLayout from 'containers/components/layouts/ContentDetailLayout';
import FiltersLayout from 'containers/components/layouts/FiltersLayout';
import EntityTypeSidebar from 'containers/components/EntityTypeSidebar';
import FreeTrialWarning from 'containers/components/FreeTrialWarning';

import Header from './Header';
import Menu from './Menu';
import Content from './Content';
import CONFIG from './TableConfig';
import SalesOrdersDetail from './SalesOrdersDetail';
import SalesOrdersCrud from './SalesOrdersCrud';

import './styles.scss';

const propTypes = {
    login: PropTypes.bool.isRequired,
};

const Main = memo(
    ({
        login,
        openDetail,
        isPageForbidden,
        permissions,
        openCrud,
        enableSalesOrdersTypes,
        getSalesRecordsStatistics,
    }) => {
        const theme = useTheme();
        const { hasTabsEnabled } = useEntityDetail();

        const sidebarProps = useMemo(() => {
            return {
                fieldName: 'salesOrderType',
                list: 'tblTiposSalesOrders',
                getInfo: (value) => {
                    return getSalesRecordsStatistics(value).then((data) =>
                        data
                            ? [
                                  {
                                      label: (
                                          <Text color={theme.colors.utility.textSecondary}>
                                              {getLiteral('label_salesrecords_footer_total_value')}
                                          </Text>
                                      ),
                                      value: formatCompactDecimalByConfig({
                                          value: data.Result.TotalValue || 0,
                                          type: DECIMAL_TYPES.CURRENCY_GENERAL,
                                          entityName: SALESORDERS.extraFieldName,
                                      }),
                                  },
                                  {
                                      label: (
                                          <Text color={theme.colors.utility.textSecondary}>
                                              {getLiteral(
                                                  'label_salesrecords_footer_total_created',
                                              )}
                                          </Text>
                                      ),
                                      value: formatCompactDecimalByConfig({
                                          value: data.Result.TotalCreated || 0,
                                          type: DECIMAL_TYPES.GENERAL,
                                          entityName: SALESORDERS.extraFieldName,
                                      }),
                                  },
                              ]
                            : null,
                    );
                },
            };
        }, [getSalesRecordsStatistics, theme]);

        if (!login) return null;
        if (isPageForbidden(['salesOrders'])) return null;

        const config = CONFIG();

        return (
            <FiltersProvider>
                <ContentLayout header={<Header />}>
                    <DetailLayout
                        header={<Menu config={config} />}
                        postHeader={
                            <FreeTrialWarning entity={SALESORDERS} addonName="addon_salesorders" />
                        }
                    >
                        <FiltersLayout
                            config={config}
                            showSidebar={enableSalesOrdersTypes}
                            sidebar={
                                <EntityTypeSidebar
                                    entity={config.entity}
                                    hideInfoTooltip
                                    {...sidebarProps}
                                />
                            }
                        >
                            <Content />
                        </FiltersLayout>
                    </DetailLayout>
                    <SalesOrdersDetail />
                    <SalesOrdersCrud />
                    <Routes>
                        <Route path="/cards" element={<Navigate to=".." replace />} />
                        <Route
                            path="/new"
                            element={
                                <RouteOnMount
                                    onMount={() => {
                                        if (!permissions || !permissions.create) return;
                                        openCrud({ entity: SALESORDERS });
                                    }}
                                />
                            }
                        />
                        <Route
                            path="/:id/edit"
                            element={
                                <RouteOnMount
                                    onMount={(params) => {
                                        if (!params.id || !permissions || !permissions.update)
                                            return;
                                        openCrud({
                                            entity: SALESORDERS,
                                            id: params.id,
                                            isFromTab: hasTabsEnabled,
                                        });
                                    }}
                                />
                            }
                        />
                        <Route
                            path="/:id"
                            element={
                                <RouteOnMount
                                    onMount={(params) => {
                                        if (!params.id) return;
                                        openDetail(SALESORDERS, params.id, true);
                                    }}
                                />
                            }
                        />
                    </Routes>
                </ContentLayout>
            </FiltersProvider>
        );
    },
);

function mapStateToProps(state) {
    return {
        login: state.config.login,
        permissions: state.config.permission.crud_permission[SALESORDERS.permission],
        enableSalesOrdersTypes: state.config.userData.enableSalesOrdersTypes,
    };
}

const mapDispatchToProps = (dispatch) => {
    return {
        getSalesRecordsStatistics: bindActionCreators(OrdersActions, dispatch)
            .getSalesRecordsStatistics,
        openDetail: bindActionCreators(EntityDetailActions, dispatch).open,
        isPageForbidden: bindActionCreators(EntityActions, dispatch).isPageForbidden,
        openCrud: bindActionCreators(EntityCrudActions, dispatch).init,
    };
};

Main.propTypes = propTypes;

export default connect(mapStateToProps, mapDispatchToProps)(Main);
